import { API_V2 } from '../lib/axios';
import { getAuthToken } from '../lib/axios/auth-token';
import { fetchAllPaginated } from './utils/fetch-all-paginated';

const CHATS_ENDPOINT = '/chats';

interface Chat {
  _id: string;
  sessionId: string;
  agentType?: string;
  title?: string;
  messages: any[];
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface GetChatsParams {
  searchAttribute: 'sessionId';
  searchType: 'in';
  search: string[];
}

const getChats = async () => {
  return fetchAllPaginated<Chat>(API_V2, CHATS_ENDPOINT);
};

const getChatById = async (sessionId: string) => {
  const response = await API_V2.get<Chat[]>(CHATS_ENDPOINT, {
    params: {
      searchAttribute: 'sessionId',
      search: sessionId
    }
  });
  return response.data[0];
};

const createChat = async (agentType?: string | null) => {
  const payload = agentType ? { agentType } : {};
  const response = await API_V2.post<Chat>(CHATS_ENDPOINT, payload);
  return response.data;
};

const updateChat = async (sessionId: string, data: { agentType?: string; title?: string }) => {
  const response = await API_V2.put<Chat>(`${CHATS_ENDPOINT}/${sessionId}`, data);
  return response.data;
};

const deleteChat = async (sessionId: string) => {
  const chat = await getChatById(sessionId);
  if (chat) {
    const response = await API_V2.delete(CHATS_ENDPOINT, {
      params: {
        ids: chat._id,
        sessionId: sessionId
      }
    });
    return response.data;
  }
  throw new Error('Chat not found');
};

/**
 * Analytics ask endpoint response
 */
interface AnalyticsAskResponse {
  success: boolean;
  question: string;
  intent: string;
  summary?: string;
  keyInsights?: string[];
  sessionId: string;
  metadata: {
    llmCalls: number;
    toolCalls: number;
    duration: number;
  };
  dataIncomplete?: boolean;
  totalRows?: number;
}

/**
 * Drilldown ask endpoint response
 */
interface DrilldownAskResponse extends AnalyticsAskResponse {
  chatId: string;
}

/**
 * Send a question about drilldown report data to the AI workflow
 * Used for drilldown chat drawer
 */
const askDrilldown = async (
  drilldownReportId: string,
  question: string,
  sessionId: string,
  chatId?: string
): Promise<DrilldownAskResponse> => {
  const response = await API_V2.post<DrilldownAskResponse>(`${CHATS_ENDPOINT}/drilldown/ask`, {
    drilldownReportId,
    question,
    sessionId,
    ...(chatId && { chatId }),
  });
  return response.data;
};

/**
 * SSE progress event from drilldown stream
 */
interface DrilldownSSEProgress {
  step: string;
  message: string;
  data?: Record<string, any>;
  timestamp: number;
}

/**
 * Callbacks for drilldown stream events
 */
interface DrilldownStreamCallbacks {
  onProgress?: (event: DrilldownSSEProgress) => void;
  onComplete?: (result: DrilldownAskResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Stream drilldown analysis via SSE for real-time progress feedback.
 * Returns an abort function to cancel the stream.
 */
const streamDrilldown = (
  drilldownReportId: string,
  question: string,
  sessionId: string,
  callbacks: DrilldownStreamCallbacks,
  chatId?: string
): (() => void) => {
  const controller = new AbortController();

  const baseURL =
    process.env.NEXT_PUBLIC_API_V2_BASE_URL ||
    (process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
      ? 'https://admin-lb.clickster.io/api/v2'
      : 'https://s-admin-lb.clickster.io/api/v2');

  const drilldownToken = getAuthToken();
  fetch(`${baseURL}${CHATS_ENDPOINT}/drilldown/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(drilldownToken ? { Authorization: `Bearer ${drilldownToken}` } : {}),
    },
    credentials: 'include',
    signal: controller.signal,
    body: JSON.stringify({
      drilldownReportId,
      question,
      sessionId,
      ...(chatId && { chatId }),
    }),
  })
    .then(async (response) => {
      if (!response.ok || !response.body) {
        callbacks.onError?.(`Request failed: ${response.status}`);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // Declare outside the loop so state persists across chunk boundaries
      let currentEvent = '';
      let currentData = '';

      const dispatchEvent = () => {
        if (currentEvent && currentData) {
          try {
            const parsed = JSON.parse(currentData);
            if (currentEvent === 'progress') {
              callbacks.onProgress?.(parsed);
            } else if (currentEvent === 'done') {
              callbacks.onComplete?.(parsed);
            } else if (currentEvent === 'error') {
              callbacks.onError?.(parsed.message);
            }
          } catch {
            // Skip malformed events
          }
          currentEvent = '';
          currentData = '';
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7);
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6);
          } else if (line === '') {
            dispatchEvent();
          }
        }
      }

      // Process any event still in the buffer after the stream ends (no trailing blank line)
      if (buffer.trim()) {
        const remainingLines = buffer.split('\n');
        for (const line of remainingLines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7);
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6);
          }
        }
      }
      dispatchEvent();
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        callbacks.onError?.(err.message || 'Stream connection failed');
      }
    });

  return () => controller.abort();
};

/**
 * SSE node event from analytics stream
 */
interface AnalyticsStreamStepDetail {
  type: string;
  instruction: string;
  reason?: string;
  query?: Record<string, any>;
  data?: Array<Record<string, any>>;
  rowCount?: number;
  totalCount?: number;
  error?: string;
}

interface AnalyticsStreamNodeEvent {
  node: string;
  timestamp: number;
  intent?: string;
  intentConfidence?: number;
  intentReasoning?: string;
  planningCycleCount?: number;
  steps?: Array<AnalyticsStreamStepDetail>;
  stepsExecuted?: number;
  hasDeferredSteps?: boolean;
  decision?: string;
  confidence?: number;
  reasoning?: string;
}

/**
 * SSE done event from analytics stream
 */
interface TokenUsageSummary {
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  peakPromptTokens: number;
  peakPromptAgent: string;
  contextLimit: number;
  contextUsagePercent: number;
  perAgent: Array<{
    agentName: string;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  }>;
}

interface AnalyticsStreamDoneEvent {
  success: boolean;
  intent?: string;
  summary?: string;
  keyInsights?: string[];
  recommendedActions?: string[];
  dataIncomplete?: boolean;
  totalRows?: number;
  confidence?: number;
  uncertaintyReasons?: string[];
  tokenUsage?: TokenUsageSummary;
  timestamp: number;
}

/**
 * Callbacks for analytics stream events
 */
interface AnalyticsStreamCallbacks {
  onStart?: (event: { sessionId: string; question: string }) => void;
  onNode?: (event: AnalyticsStreamNodeEvent) => void;
  onDone?: (event: AnalyticsStreamDoneEvent) => void;
  onError?: (error: string) => void;
}

/**
 * Stream analytics workflow via SSE for real-time node progress.
 * Returns an abort function to cancel the stream.
 */
const streamAnalytics = (
  question: string,
  sessionId: string,
  callbacks: AnalyticsStreamCallbacks,
): (() => void) => {
  const controller = new AbortController();

  const baseURL =
    process.env.NEXT_PUBLIC_API_V2_BASE_URL ||
    (process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
      ? 'https://admin-lb.clickster.io/api/v2'
      : 'https://s-admin-lb.clickster.io/api/v2');

  const analyticsToken = getAuthToken();
  fetch(`${baseURL}${CHATS_ENDPOINT}/analytics/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(analyticsToken ? { Authorization: `Bearer ${analyticsToken}` } : {}),
    },
    credentials: 'include',
    signal: controller.signal,
    body: JSON.stringify({ question, sessionId }),
  })
    .then(async (response) => {
      if (!response.ok || !response.body) {
        callbacks.onError?.(`Request failed: ${response.status}`);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';
      let currentData = '';

      const dispatchEvent = () => {
        if (currentEvent && currentData) {
          try {
            const parsed = JSON.parse(currentData);
            if (currentEvent === 'start') {
              callbacks.onStart?.(parsed);
            } else if (currentEvent === 'node') {
              callbacks.onNode?.(parsed);
            } else if (currentEvent === 'done') {
              callbacks.onDone?.(parsed);
            } else if (currentEvent === 'error') {
              callbacks.onError?.(parsed.message);
            }
          } catch {
            // Skip malformed events
          }
          currentEvent = '';
          currentData = '';
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7);
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6);
          } else if (line === '') {
            dispatchEvent();
          }
        }
      }

      if (buffer.trim()) {
        const remainingLines = buffer.split('\n');
        for (const line of remainingLines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7);
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6);
          }
        }
      }
      dispatchEvent();
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        callbacks.onError?.(err.message || 'Stream connection failed');
      }
    });

  return () => controller.abort();
};

export type {
  AnalyticsAskResponse,
  AnalyticsStreamNodeEvent,
  AnalyticsStreamDoneEvent,
  DrilldownAskResponse,
  DrilldownSSEProgress,
  AnalyticsStreamStepDetail,
};

export const ChatsService = {
  getChats,
  getChatById,
  createChat,
  updateChat,
  deleteChat,
  askDrilldown,
  streamDrilldown,
  streamAnalytics,
};
